<template>
    <MonitorLayoutTemplate
        :table_title="table_title"
        :is_show_chart_dialog="is_show_chart_dialog"
    >
        <template #datetime>
            <QpeDatetime
                :props_date="date"
                :props_time="time"
                :name="default_value"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
            />
        </template>
        
        <template #filter_condition_options>
            <MonitorOptionSet 
                ref="monitor_option_set"
                :option_set="option_set"
                @filter_data="filter_data"
            />
        </template>

        <template #data_table="slot_props">
            <QpeDatatable
                :data="table_data"
                :headers="header"
                :name="default_value"
                :error_text="error_text"
                :search="slot_props.search"
                :should_draw_diagram_header="should_draw_diagram_header"
                :enable_diagram_slot="true"
                :show_footer="!is_pub"
                :show_download_doc_btn="!is_pub"
                @open-chart="assign_chart_component"
                :is_show_chart_dialog_parent.sync="is_show_chart_dialog"
            >
                <!-- 將事件傳給子元件的 method 作法 -->
                <template #diagram_dialog="{ close_dialog }"> 
                    <GisChart v-if="is_show_chart_dialog"
                        id="belongs_monitor_chart"
                        :chart_options="chart_options"
                        :chart_type="chart_options.type"
                        :default_variable="weather_history_option"
                        :prop_rain_selected="$t('__rainfall.hour_3')"
                        @close-chart-dialog="close_dialog"
                    />
                </template>
                <template #table_description>
                    <span>
                        {{ $t('__monitor.no_data_annotaion') }}<br>
                        {{ $t('__monitor.min_10_gust_annotation') }}<br>
                        {{ $t('__monitor.hour_1_gust_annotation') }}<br>
                    </span>
                </template>
            </QpeDatatable>
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import { redirect_monitor_table_ajax, monitor_table_ajax } from "@/utils/monitor/monitor.js"
    import { sort_callback_by_city_name_tw, sort_callback_by_city_name_en, add_gust_interval_label_callback } from '@/utils/monitor/common_tool.js'
    import {
        generate_variable_options,
        combine_new_variable_options,
        update_option_set_from_local_storage,
        update_current_option_value,
        new_filter_unit_function,
    } from '@/utils/monitor/option.js'
    import { get_stations_history_params } from "@/utils/chart/chart_ajax.js"
    import { filter_same_district } from '@/utils/monitor/customization/port.js'
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import { TABLE_VALUE_TEXT_MAPPING, WEATHER_HISTORY_OPTION_MAPPING } from "@/config/monitor_config.js"
    import { mapState, mapGetters } from "vuex"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import MonitorOptionSet from "@/components/monitor/monitor_option_set.vue"
    import QpeDatatable from "@/components/monitor/qpe_datatable.vue"

    export default {
        name: 'SurfaceWeather',

        components: {
            MonitorLayoutTemplate,
            QpeDatetime,
            MonitorOptionSet, 
            QpeDatatable, 
            GisChart: () => import('@/components/chart/gis_chart.vue'),
        },

        mixins: [monitor_mixin],

        props: {
            default_value: {
                type: Number,
                required: true,
            },

            activate_filter_by_parent: {
                type: Boolean,
                default: false
            },

            filter_value_from_parent: {
                type: String,
                default: ''
            }
        },

        data() {
            return {
                // 排序使用
                table_data: [],
                header: [],
                error_text: '',

                immutable_table_data: [],
                option_set: [],
                local_storage_option_name: 'surface_weather_option',

                should_draw_diagram_header: [
                    '風向(degree)', 'Wind Direction (degree)',
                    '平均風(m/s)', 'Wind Speed (m/s)',
                    '瞬間風(m/s)', 'Wind Gust (m/s)',
                    '溫度(°C)', 'Temperature (°C)',
                    '相對濕度(%)', 'Relative Humidity (%)',
                    '氣壓(hPa)', 'Pressure (hPa)',
                    '當日累積雨量(mm)', 'Rainfall (mm)'
                ],
                should_draw_weather_diagram_header: [
                    '風向(degree)', 'Wind Direction (degree)',
                    '平均風(m/s)', 'Wind Speed (m/s)',
                    '瞬間風(m/s)', 'Wind Gust (m/s)',
                    '溫度(°C)', 'Temperature (°C)',
                    '相對濕度(%)', 'Relative Humidity (%)',
                    '氣壓(hPa)', 'Pressure (hPa)',
                ],

                is_show_chart_dialog: false,
                chart_options: {
                    type: { 'gauge': true, 'weather': true }
                },
                weather_history_option: '',
            }
        },

        computed: {
            ...mapState([
                'language',
                'client_name',
            ]),

            ...mapGetters([
                'is_pub'
            ]),

            table_title() {
                if (this.language === 'en') {
                    return `${this.$t('__surface_weather.default_name')} Information`
                }
                return `${TABLE_VALUE_TEXT_MAPPING[this.default_value]['tag_name']}資訊`
            },

            display_all_city() {
                return this.$t('__surface_weather.display_all_city')
            }, 
            
            display_all_township() {
                return this.$t('__surface_weather.display_all_township')
            },
            
            is_display_all_table() {
                return this.is_pub
            }
        },
        
        watch: {
            immutable_table_data: {
                deep: true,
                handler() {
                    if (Object.keys(this.immutable_table_data).length === 0) return
                    
                    if (this.activate_filter_by_parent) {
                        return this.$emit('collect_data', this.immutable_table_data)
                    }
                    this.generate_option_set()
                    this.option_set = update_option_set_from_local_storage(this.option_set , this.local_storage_option_name)
                },
            },

            language() {
                this.option_set = []
                monitor_table_ajax.call(this, this.default_value, `${this.date} ${this.time}:00`)
            },

            filter_value_from_parent() {
                this.table_data = filter_same_district(this.immutable_table_data, this.filter_value_from_parent)
            }
        },
        
        created() {
            redirect_monitor_table_ajax.call(this, this.default_value)
        },

        methods: {
            generate_table_data(data, header) {
                const sort_callback = this.language === 'en' ? sort_callback_by_city_name_en : sort_callback_by_city_name_tw
                const gust_key = this.language === 'en' ? 'Wind Gust (m/s)' : '瞬間風(m/s)'
                const has_gust_interval_label_data = data
                    .sort(sort_callback)
                    .map(add_gust_interval_label_callback(gust_key))
                this.immutable_table_data = has_gust_interval_label_data;
                this.table_data = has_gust_interval_label_data;
                this.header = header;
            },

            generate_option_set() {
                const basic_variable_options = [
                    { default_value: this.display_all_city, json_key: this.$t('__surface_weather.city'), label: 'city'},
                    { default_value: this.display_all_township, json_key: this.$t('__surface_weather.township'), label: 'township'},
                ]

                const variable_options = generate_variable_options(basic_variable_options, this.immutable_table_data)
                
                if (!this.option_set.length) {
                    this.option_set = [...variable_options]
                } else {
                    this.option_set = combine_new_variable_options(this.option_set, variable_options)
                    this.$refs.monitor_option_set.change_option_set()
                }
            },

            filter_data(filtered_condition, change_condition_label) {
                const update_current_value_by_filtered_condition = update_current_option_value(filtered_condition)
                this.option_set = this.option_set.map(update_current_value_by_filtered_condition)

                if (change_condition_label === 'city') {
                    this.option_set[1].current_option_value = this.display_all_township
                    filtered_condition['township'] = this.display_all_township
                }

                const filter_same_city = new_filter_unit_function(
                    filtered_condition['city'], 
                    this.$t('__surface_weather.city'), 
                    this.display_all_city
                )
                const filter_same_township = new_filter_unit_function(
                    filtered_condition['township'], 
                    this.$t('__surface_weather.township'), 
                    this.display_all_township
                )
        
                const filtered_data = this.immutable_table_data.filter(datum => {
                    return (
                        filter_same_city(datum) &&
                        filter_same_township(datum)
                    )
                })
                this.table_data = filtered_data

                // 過濾完之後重新根據資料狀態，生成鄉鎮 option
                this.generate_towns_options(filtered_condition['city'])
                
                // 把目前選擇的 option 存起來
                localStorage.setItem('surface_weather_option', JSON.stringify(filtered_condition))
            },

            generate_towns_options(city) {                
                const filter_towns_by_city = item => item[this.$t('__surface_weather.city')] === city
                const get_town = item => item[this.$t('__surface_weather.township')]
                const filtered_towns_options = city === this.display_all_city ? this.immutable_table_data : this.immutable_table_data.filter(filter_towns_by_city)
                const towns_options = filtered_towns_options.map(get_town)
                this.option_set[1].option_array = [this.display_all_township, ...new Set(towns_options)]
            },

            assign_chart_component(header_name, choosed_value, current_row_data) {
                this.chart_options = { 
                    ...this.chart_options, 
                    ...get_stations_history_params.call(this, current_row_data) 
                }

                const special_weather_history_option = WEATHER_HISTORY_OPTION_MAPPING[header_name]
                if (special_weather_history_option) {
                    return this.weather_history_option = special_weather_history_option
                } 
                    
                this.weather_history_option = header_name.split('(')[0].trim()
            }
        },
    }
</script>